import { mimeWrapper } from "@lib/mime";
import { findLowQualityVideoPreview } from "@services/analysis";
import { presignObjectAPI } from "@modules/apis/s3";

// bucket 을 host 앞머리에서 뽑을 수 있는 virtual-hosted 스타일 S3 호스트 (bucket.s3[...].amazonaws.com)
const S3_VIRTUAL_HOSTED_HOST = /^[^.]+\.s3[.-][^.]*\.?amazonaws\.com$/i;

// SigV4 presigned URL 의 서명 시각 (예: 20260819T093006Z)
const SIGV4_DATE = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;

// 재생에 필요한 시간에 더해 이만큼은 남아 있어야 재발급을 건너뛴다.
// (버퍼링·일시정지·되감기로 재생이 영상 길이보다 오래 걸리는 몫)
const PRESIGN_EXPIRY_MARGIN_MS = 5 * 60 * 1000;

/**
 * SigV4 presigned URL 의 남은 유효 시간을 구한다.
 *
 * @param {URL} url
 * @param {number} now - 기준 시각 (epoch ms)
 * @returns {number|null} 남은 밀리초. 서명 파라미터가 없거나 형식이 다르면 null(판단 불가)
 */
const getPresignedRemainingMs = (url, now) => {
    const signedAt = SIGV4_DATE.exec(url.searchParams.get("X-Amz-Date") ?? "");
    // Number(null) / Number("") 는 0 이라 finite 검사만으로는 파라미터 누락을 못 거른다.
    const expiresParam = url.searchParams.get("X-Amz-Expires");
    const expiresInSeconds = Number(expiresParam);
    if (!signedAt || !expiresParam || !Number.isFinite(expiresInSeconds)) return null;

    const [, year, month, day, hour, minute, second] = signedAt;
    const signedAtMs = Date.parse(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
    if (!Number.isFinite(signedAtMs)) return null;

    return signedAtMs + expiresInSeconds * 1000 - now;
};

/**
 * URL 에서 서명 쿼리를 뺀 경로만 뽑는다. 같은 소스인지 비교할 때 쓴다.
 *
 * @param {string} url
 * @returns {string|undefined} 파싱할 수 없으면 undefined
 */
const getUrlPath = (url) => {
    try {
        return new URL(url).pathname;
    } catch (error) {
        return undefined;
    }
};

/**
 * URL 의 경로에서 S3 오브젝트 키를 뽑는다.
 *
 * URL.pathname 은 퍼센트 인코딩된 값이라(한글·공백 파일명) 그대로 넘기면 엉뚱한 키로 서명돼
 * 재발급된 URL 이 다시 403 이 된다. 다른 presign 호출부(PropertyPanel 등)와 같이 디코딩해서 넘긴다.
 *
 * @param {string} pathname
 * @returns {string}
 */
const getObjectKeyFromPathname = (pathname) => {
    const key = pathname.replace(/^\/+/, "");
    try {
        return decodeURIComponent(key);
    } catch (error) {
        // 인코딩이 깨진 경로는 판단을 백엔드에 맡기고 원문 그대로 넘긴다.
        return key;
    }
};

/**
 * presigned 쿼리스트링이 붙은 URL을 그대로 넘기면 mime 이 확장자를 잘못 읽어 text/plain 을 돌려준다.
 * (예: ...proxy.mp4?X-Amz-Signature=abc.def → text/plain)
 * 경로만 잘라 판별한다.
 *
 * @param {string} url
 * @returns {string|undefined}
 */
const getMimeTypeFromUrlPath = (url) => {
    const path = getUrlPath(url);
    return (path && mimeWrapper.getMimeTypeFromUrl(path)) ?? undefined;
};

/**
 * 비디오 플레이어에 넘길 재생 소스 목록을 만든다.
 *
 * 원본은 faststart(moov atom 선두 배치)가 아닌 경우가 많다. 그러면 브라우저가 moov 를 찾을 때까지
 * Range 요청(206)을 반복하고, 그동안 loadedmetadata 가 뜨지 않아 총 재생시간이 표시되지 않는다.
 * 트랜스코딩된 저화질 프리뷰가 있으면 목록 맨 앞에 두어 기본 재생 대상으로 삼는다.
 * (ai-workspace 가 editableSource 에서 쓰는 정책과 동일 — services/analysis.js 의 findPreviewOrOriginal)
 *
 * @param {Object} asset - duration / accessUrls / previews 를 가진 에셋
 * @returns {Array<{accessUrl: string, duration: number, mimeType?: string, isPreview?: boolean}>}
 *   프리뷰가 있으면 [프리뷰, ...원본] 순서. 없으면 기존과 동일하게 원본 목록만.
 */
export const createVideoPlayInfos = (asset) => {
    const duration = asset?.duration;
    const originals = (asset?.accessUrls ?? []).map((accessUrl) => ({ accessUrl, duration }));

    // 암호화(DRM) 에셋은 라이선스가 원본 패키징 기준이라 프리뷰로 바꾸면 재생이 깨진다.
    // (CloudPlexVideoPlayer 의 isDRMContent → createDrmToken 경로)
    if (asset?.encryptions?.length > 0) return originals;

    const preview = findLowQualityVideoPreview(asset);
    if (!preview?.accessUrl) return originals;

    // 호출부가 이미 프리뷰를 accessUrls 로 넘긴 경우(ai-workspace 의 editableSource)가 있어
    // 같은 소스가 목록에 두 번 들어가지 않게 걸러낸다. presigned 서명(쿼리)은 조회마다 바뀌므로 경로로 비교한다.
    // 경로를 못 읽으면(비정상 URL) 비교가 undefined 끼리 맞아떨어져 원본이 통째로 걸러지므로 그대로 둔다.
    const previewPath = getUrlPath(preview.accessUrl);
    const originalsWithoutPreview = previewPath
        ? originals.filter(({ accessUrl }) => getUrlPath(accessUrl) !== previewPath)
        : originals;

    return [
        {
            accessUrl: preview.accessUrl,
            duration,
            // 원본이 MOV 여도 프리뷰는 트랜스코딩 산출물이라 타입이 다르다. URL 기준으로 따로 잡아준다.
            mimeType: getMimeTypeFromUrlPath(preview.accessUrl),
            isPreview: true,
        },
        ...originalsWithoutPreview,
    ];
};

/**
 * 재발급 API 로 다시 서명받을 수 있는 URL 인지 본다.
 *
 * 쿼리가 없으면 서명되지 않은 URL(CDN 등)이라 재발급 대상이 아니다.
 * bucket 을 host 앞머리에서 뽑는 것도 virtual-hosted 스타일(bucket.s3.region.amazonaws.com)에서만 맞다 —
 * CloudFront 서명 URL 이나 path-style 이면 엉뚱한 값이 나가 호출이 무의미하게 실패한다.
 *
 * @param {URL} url
 * @returns {boolean}
 */
const isRefreshable = (url) => url.search.length > 0 && S3_VIRTUAL_HOSTED_HOST.test(url.host);

/**
 * 재발급을 시도해볼 만한 URL 인지 판단한다.
 *
 * 재생 중 403 복구에서, 재발급해도 같은 URL 이 돌아올 상황을 미리 걸러내는 데 쓴다.
 *
 * @param {string} accessUrl
 * @returns {boolean}
 */
export const isRefreshablePresignedUrl = (accessUrl) => {
    if (!accessUrl) return false;
    try {
        return isRefreshable(new URL(accessUrl));
    } catch (error) {
        return false;
    }
};

/**
 * 지금 이 서명으로 playbackDurationMs 만큼 재생을 마칠 수 있는지 보고 재발급 필요 여부를 정한다.
 *
 * @param {URL} url
 * @param {number} [playbackDurationMs]
 * @returns {boolean}
 */
const needsRefresh = (url, playbackDurationMs) => {
    // 남은 시간을 못 읽으면(SigV4 가 아닌 서명 등) 안전하게 재발급한다.
    const remainingMs = getPresignedRemainingMs(url, Date.now());
    if (remainingMs === null) return true;

    const requiredMs =
        (Number.isFinite(playbackDurationMs) ? Math.max(playbackDurationMs, 0) : 0) + PRESIGN_EXPIRY_MARGIN_MS;
    return remainingMs <= requiredMs;
};

/**
 * 재발급을 기다리지 않고 이 URL 로 곧바로 재생을 시작해도 되는지 판단한다.
 *
 * 어차피 재발급될 URL 로 먼저 재생을 시작하면, 잠시 뒤 소스가 교체되면서 이미 받던 미디어를 버리고
 * 처음부터 다시 로드한다(상세 진입마다 소스 2회 로드). 소비처가 "재서명을 기다릴지" 를 정할 때 쓴다.
 *
 * @param {string} accessUrl
 * @param {number} [playbackDurationMs] - 이 URL 로 재생해야 하는 길이(ms)
 * @returns {boolean} refreshPresignedUrl 이 같은 URL 을 돌려줄 상황이면 true
 */
export const canPlayWithoutRefresh = (accessUrl, playbackDurationMs) => {
    if (!accessUrl) return false;
    try {
        const url = new URL(accessUrl);
        // 재발급 대상이 아니면 refreshPresignedUrl 도 그대로 돌려주므로 교체가 일어나지 않는다.
        if (!isRefreshable(url)) return true;
        return !needsRefresh(url, playbackDurationMs);
    } catch (error) {
        // 파싱할 수 없는 URL 도 refreshPresignedUrl 이 그대로 돌려주므로 교체가 일어나지 않는다.
        return true;
    }
};

/**
 * presigned accessUrl 을 재발급받는다.
 *
 * 상세/목록 응답의 accessUrl 은 조회 시점에 서명된 것이고 수명이 짧다(X-Amz-Expires=600 → 10분).
 * 만료된 URL 로 재생을 시작하면 S3 가 403 을 주고, 플레이어가 소스를 계속 재요청하다 타임아웃돼
 * "지원하지 않는 영상"으로 보인다. 재생에 필요한 만큼 남아있지 않으면 다시 서명해 이를 막는다.
 * (ai-workspace 에 CM-15804 로 먼저 적용됐던 로직을 공통화한 것)
 *
 * 서명이 충분히 남았으면 재발급하지 않고 같은 URL 을 돌려준다. 재발급은 매번 다른 서명을 만들어
 * URL 을 바꾸는데, 그러면 소비처가 소스를 교체하면서 플레이어를 다시 만들고 이미 받던 미디어를 버린다.
 *
 * ⚠️ X-Amz-Expires 로 계산한 남은 시간은 상한일 뿐 실제 수명이 아니다. 이 URL 들은 임시 자격증명
 * (X-Amz-Security-Token)으로 서명돼 있어, 그 자격증명이 먼저 만료되면 남은 시간과 무관하게 403 이 된다.
 * 그래서 진입 시 재발급만으로는 부족하고 재생 중 403 복구가 함께 필요하다
 * (@hooks/usePresignedPlaybackRecovery — 그 복구 경로가 force 를 쓴다).
 *
 * @param {string} accessUrl
 * @param {{apiEndpoint: string, stageVersion: string, projectId: string}} context
 * @param {Object} [options]
 * @param {number} [options.playbackDurationMs] - 이 URL 로 재생해야 하는 길이(ms). 남은 시간이 이보다
 *   짧으면 재생 도중 만료되므로 미리 재발급한다. 없으면 여유 시간만 본다.
 * @param {boolean} [options.force] - 남은 시간과 무관하게 재발급한다. 이미 403 을 받아 쿼리의
 *   남은 시간을 믿을 수 없는 복구 경로에서 쓴다.
 * @returns {Promise<string>} 재발급된 URL. 재발급이 불필요하거나 실패하면 원래 URL 을 그대로 돌려준다.
 */
export const refreshPresignedUrl = async (
    accessUrl,
    { apiEndpoint, stageVersion, projectId },
    { playbackDurationMs, force = false } = {},
) => {
    try {
        const url = new URL(accessUrl);
        if (!isRefreshable(url)) return accessUrl;

        if (!force && !needsRefresh(url, playbackDurationMs)) return accessUrl;

        const bucket = url.host.split(".")[0];
        const fileName = getObjectKeyFromPathname(url.pathname);
        const response = await presignObjectAPI(apiEndpoint, stageVersion, projectId, { bucket, fileName });
        // 재발급 실패 시 undefined 로 덮어써 재생이 아예 안 되는 일이 없도록 원본으로 폴백한다.
        return response.data?.presignedUrl ?? accessUrl;
    } catch (error) {
        console.error(error);
        return accessUrl;
    }
};

/**
 * 재생 소스 목록을 만들고, 기본 재생 대상(첫번째)의 presigned URL 을 재발급해 돌려준다.
 *
 * 나머지 소스는 선택될 때 재발급하지 않는다 — 기본 재생 대상만 갱신하는 edit-video 와 동일한 범위다.
 * 영상 길이만큼 버티지 못할 서명이면 미리 재발급한다. 그래도 재생 도중 만료될 수 있어
 * 최종 방어는 usePresignedPlaybackRecovery 가 맡는다.
 *
 * @param {Object} asset - duration / accessUrls / previews 를 가진 에셋
 * @param {{apiEndpoint: string, stageVersion: string, projectId: string}} context
 * @returns {Promise<Array>} createVideoPlayInfos 와 같은 형태. 첫번째 항목의 accessUrl 만 재발급된다.
 */
export const resolveVideoPlayInfos = async (asset, context) => {
    const [first, ...rest] = createVideoPlayInfos(asset);
    if (!first) return [];

    const accessUrl = await refreshPresignedUrl(first.accessUrl, context, { playbackDurationMs: first.duration });
    return [{ ...first, accessUrl }, ...rest];
};
